// Client-side arena state (lane C; docs/territory-arena.md §11, protocol §5.3–§5.4).
//
// Built from the WELCOME arena block and fed every FRAME's arena sections on arrival. Two time
// domains (backlog P-3; paperio shows a cut and the contact in the same frame):
// - the own life's changes apply on arrival (its head is predicted ahead of the server): a frame
//   with an own CAPTURE, DEATH (as victim or killer), COLLECT or EXIT, or a land snapshot, applies
//   its land, trails, drops and events at once (after every older deferred frame), as do the own
//   trail records of any frame;
// - every other frame's land, trails, drops and events wait until the render tick reaches the
//   frame's tick (`advance(renderTick)`, once per drawn frame), because remote heads are drawn
//   that far behind: a remote cut or capture shows when the drawn head gets there, not 60–160 ms
//   before it. A head missing from a HEADS section stays drawn until then too. The wait is capped
//   at DEFER_MAX_TICKS behind the newest frame (no `advance` while hidden, tests).
// Sections:
// - land: a per-cell owner (player id) grid for the whole map; ARENA_LAND chunks replace 16 × 16
//   blocks (a snapshot clears first). Each change bumps the chunk's version and its 8 neighbours'
//   (their contours read a 1-cell border), which is what the land layer's cache compares;
// - heads: up to HEAD_SAMPLES server samples per player (tick-stamped) for 60 fps interpolation
//   at `renderTick` (game/clock.ts); the own head is drawn from the predictor instead. A HEADS
//   section lists every head in view, so heads missing from one are dropped; heads not refreshed
//   for HEAD_STALE_TICKS are dropped too (a frame without the section);
// - trails: per player the ordered cell list (APPEND continues, RESET replaces, CLEAR empties and
//   bumps the trail's epoch);
// - drops: id → cell and value; removals are kept for one drain (collect effects);
// - events: a queue stamped with the frame's tick and arrival time, drained once per rendered
//   frame by the arena client (HUD, effects, audio); stale ones (> ARENA_CLIENT.staleEventMs
//   when drained) are dropped by the consumers, never replayed (C-15, C-23);
// - own: land cells, kills, the last applied input seq (prediction), land percent;
// - minimap (ARENA_MINIMAP) and the money values / names / colours it needs from PLAYERS and
//   MONEY, so the arena drawing never reaches into the cell WorldStore.
// Pure data and maths: no DOM, testable with plain frames.

import {
  ARENA_CLIENT,
  ARENA_EVENT,
  ARENA_NET,
  ARENA_TRAIL_OP,
  type ArenaDropRemove,
  type ArenaEventRecord,
  type ArenaHeadRecord,
  applyChunkRuns,
  applyTrailSteps,
  chunksAlong,
  expandRuns,
  type FrameMessage,
  headingFromWire,
  INTERPOLATION,
  landPctHundredths,
  type LeaderboardSection,
  type Rgb,
  type WelcomeArenaInfo,
} from '@ballio/shared';

/**
 * Server samples kept per head: the render tick may sit up to INTERPOLATION.delayMaxMs (4 ticks
 * at 25 Hz) behind the newest frame, so the history spans that plus 2 ticks of slack for late
 * frames (fewer, and a head clamped to its oldest sample stands still: 25 Hz stair-steps).
 */
export const HEAD_SAMPLES = Math.max(8, Math.ceil(INTERPOLATION.delayMaxMs / 40) + 2);
/** A head not refreshed for this many ticks is gone (out of view, or the section was omitted). */
export const HEAD_STALE_TICKS = 10;
/** Extrapolation past the newest head sample, at most (ticks). */
export const HEAD_EXTRAPOLATE_TICKS = 1;
/**
 * Remote world changes wait for the render tick at most this many ticks behind the newest frame
 * (the clock's delay cap, INTERPOLATION.delayMaxMs = 4 ticks, plus one).
 */
export const DEFER_MAX_TICKS = Math.ceil(INTERPOLATION.delayMaxMs / 40) + 1;

/** One frame's world changes waiting for the render tick (P-3). */
interface DeferredWorld {
  readonly tick: number;
  readonly atMs: number;
  readonly land: NonNullable<FrameMessage['arenaLand']> | undefined;
  readonly trails: NonNullable<FrameMessage['arenaTrails']>;
  readonly drops: NonNullable<FrameMessage['arenaDrops']> | undefined;
  readonly events: readonly ArenaEventRecord[];
}

export interface ArenaPlayerInfo {
  readonly id: number;
  readonly name: string;
  readonly color: Rgb;
  readonly isBot: boolean;
  /** Arena skin id (`t:<slug>`) or `""`; ids the client doesn't know draw as no skin. */
  readonly skinId: string;
}

export interface HeadSample {
  tick: number;
  x: number;
  y: number;
  /** Radians [0, 2π). */
  heading: number;
}

export interface ArenaHead {
  readonly playerId: number;
  /** Oldest first. */
  readonly samples: HeadSample[];
  shielded: boolean;
  cashingOut: boolean;
  frozen: boolean;
  autopilot: boolean;
  alarm: boolean;
  progress: number;
  /** Frame tick this head was last listed in. */
  lastTick: number;
  /** Frame tick this head first appeared (spawn effects). */
  readonly firstTick: number;
  /**
   * The frame tick whose HEADS section no longer listed this head (it died, left or went out of
   * view): it is dropped once the render tick reaches it. −1 while listed.
   */
  goneTick: number;
}

export interface ArenaTrail {
  readonly cells: number[];
  /** +1 on every RESET and CLEAR: the rendered path history restarts. */
  epoch: number;
}

export interface ArenaDrop {
  readonly dropId: number;
  readonly cell: number;
  readonly value: number;
  /** Arrival time (pop-in animation). */
  readonly atMs: number;
}

export interface QueuedArenaEvent {
  readonly event: ArenaEventRecord;
  readonly tick: number;
  /** performance.now() at the frame's arrival. */
  readonly atMs: number;
}

export interface RemovedDrop extends ArenaDropRemove {
  readonly cell: number;
  readonly value: number;
  readonly atMs: number;
}

export interface ArenaOwnState {
  playerId: number;
  landCells: number;
  kills: number;
  inputSeq: number;
  /** Land percent, 2 decimals (0–100). */
  landPct: number;
  /** The best land percent this life (HUD bar, results). */
  maxLandPct: number;
  /** Own head's ALARM flag (minimap rim, optional sound). */
  alarm: boolean;
  /** The newest own head record from the server and its frame tick (predictor input). */
  head: ArenaHeadRecord | null;
  headTick: number;
  /** Bumped when `head` changes. */
  headVersion: number;
}

/**
 * What a dead player looked like the frame it died (the land and trail vanish in that frame):
 * the break-up particles start from it (visuals §9).
 */
export interface DeathSnapshot {
  readonly victimId: number;
  readonly color: Rgb;
  /** Land boundary edges, flat `[x, y, nx, ny, …]` (edge midpoint and outward normal), capped. */
  readonly edges: number[];
  /** The trail's cells at death. */
  readonly trail: number[];
}

/** Boundary edges kept per death snapshot (the particle cap is 1,500 per death). */
export const DEATH_EDGES_MAX = 3000;

export interface ArenaMinimapState {
  size: number;
  /** size × size owner ids, row-major. */
  cells: number[];
  heads: { playerId: number; x: number; y: number; isYou: boolean }[];
  version: number;
}

const TAU = Math.PI * 2;

function lerpAngle(a: number, b: number, t: number): number {
  let d = (b - a) % TAU;
  if (d > Math.PI) d -= TAU;
  else if (d < -Math.PI) d += TAU;
  const r = (a + d * t) % TAU;
  return r < 0 ? r + TAU : r;
}

export class ArenaStore {
  readonly info: WelcomeArenaInfo;
  readonly cols: number;
  readonly rows: number;
  readonly chunksX: number;
  readonly chunksY: number;
  /** Owner player id per cell (0 = neutral), `cy · cols + cx`. */
  readonly owner: Uint32Array;
  /** Per chunk: bumped on every change touching it or a neighbour (the land layer's cache key). */
  readonly chunkVersion: Uint32Array;
  /** Bumped on a land snapshot (the land layer drops its whole cache). */
  landEpoch = 0;
  /** Bumped on every land change. */
  landVersion = 0;

  readonly players = new Map<number, ArenaPlayerInfo>();
  /** Bumped when a player is announced or changes colour (the land cache redraws). */
  playersVersion = 0;
  /** Lamports per player (MONEY section). */
  readonly values = new Map<number, number>();
  solUsd = 0;
  readonly heads = new Map<number, ArenaHead>();
  readonly trails = new Map<number, ArenaTrail>();
  readonly drops = new Map<number, ArenaDrop>();
  leaderboard: LeaderboardSection | null = null;
  leaderboardVersion = 0;
  readonly own: ArenaOwnState = {
    playerId: 0,
    landCells: 0,
    kills: 0,
    inputSeq: 0,
    landPct: 0,
    maxLandPct: 0,
    alarm: false,
    head: null,
    headTick: -1,
    headVersion: 0,
  };
  readonly minimap: ArenaMinimapState = { size: 0, cells: [], heads: [], version: 0 };
  /** Newest applied frame tick (−1 before any). */
  lastTick = -1;
  lastFrameAtMs = Number.NEGATIVE_INFINITY;

  private events: QueuedArenaEvent[] = [];
  /** Remote frames' world changes, oldest first, until the render tick reaches them. */
  private deferred: DeferredWorld[] = [];
  private removedDrops: RemovedDrop[] = [];
  private deaths = new Map<number, DeathSnapshot>();
  /** Land cells of players that exited (cash-out, forced exit) in a frame since the last drain. */
  private exits = new Map<number, { readonly color: Rgb; readonly cells: number[] }>();
  /** Cells the own player gained in the newest frame with an own CAPTURE (the capture flash). */
  ownGain: { cells: number[]; atMs: number } | null = null;

  constructor(info: WelcomeArenaInfo) {
    this.info = info;
    this.cols = Math.max(1, info.cols | 0);
    this.rows = Math.max(1, info.rows | 0);
    this.chunksX = chunksAlong(this.cols);
    this.chunksY = chunksAlong(this.rows);
    this.owner = new Uint32Array(this.cols * this.rows);
    this.chunkVersion = new Uint32Array(this.chunksX * this.chunksY);
  }

  /** The player id this client plays (0 = none). */
  setOwn(playerId: number): void {
    if (this.own.playerId === playerId) return;
    const o = this.own;
    o.playerId = playerId;
    o.landCells = 0;
    o.kills = 0;
    o.landPct = 0;
    o.maxLandPct = 0;
    o.alarm = false;
    o.head = null;
    o.headTick = -1;
    o.headVersion++;
  }

  /** Applies one FRAME's arena sections (and PLAYERS, LEADERBOARD, MONEY as arena rooms use them). */
  applyFrame(frame: FrameMessage, nowMs: number): void {
    const tick = frame.tick;
    if (tick < this.lastTick) this.resetDynamic();
    this.lastTick = tick;
    this.lastFrameAtMs = nowMs;

    if (frame.players) {
      for (const p of frame.players) {
        const old = this.players.get(p.playerId);
        // The land bitmaps depend on colour and skin (pattern), so either change redraws them.
        if (!old || old.color !== p.color || old.skinId !== p.skinId) this.playersVersion++;
        this.players.set(p.playerId, {
          id: p.playerId,
          name: p.name,
          color: p.color,
          isBot: p.isBot,
          skinId: p.skinId,
        });
        if (p.isYou && this.own.playerId === 0) this.setOwn(p.playerId);
      }
    }
    if (frame.money) {
      if (frame.money.solUsd > 0) this.solUsd = frame.money.solUsd;
      for (const v of frame.money.values) this.values.set(v.playerId, v.value);
    }
    if (frame.leaderboard) {
      this.leaderboard = frame.leaderboard;
      this.leaderboardVersion++;
    }
    this.applyHeads(frame.arenaHeads, tick);
    const world: DeferredWorld = {
      tick,
      atMs: nowMs,
      land: frame.arenaLand,
      trails: frame.arenaTrails ?? [],
      drops: frame.arenaDrops,
      events: frame.arenaEvents ?? [],
    };
    if (world.land?.snapshot === true || world.events.some((e) => this.ownsEvent(e))) {
      this.flushDeferred();
      this.applyWorld(world);
    } else {
      const own = this.own.playerId;
      const ownTrails = own === 0 ? [] : world.trails.filter((r) => r.playerId === own);
      if (ownTrails.length > 0) this.applyTrails(ownTrails);
      const rest = ownTrails.length > 0 ? world.trails.filter((r) => r.playerId !== own) : null;
      const remote = rest !== null ? { ...world, trails: rest } : world;
      const empty =
        remote.land === undefined &&
        remote.trails.length === 0 &&
        remote.drops === undefined &&
        remote.events.length === 0;
      if (!empty) this.deferred.push(remote);
      this.flushUpTo(tick - DEFER_MAX_TICKS);
    }
    if (frame.arenaOwn) {
      const o = this.own;
      o.landCells = frame.arenaOwn.landCells;
      o.kills = frame.arenaOwn.kills;
      o.inputSeq = frame.arenaOwn.inputSeq;
      o.landPct = landPctHundredths(o.landCells, this.info.playableCells) / 100;
      if (o.landPct > o.maxLandPct) o.maxLandPct = o.landPct;
    }
    if (frame.arenaMinimap) {
      const mm = frame.arenaMinimap;
      const m = this.minimap;
      m.size = mm.size;
      const n = mm.size * mm.size;
      if (m.cells.length !== n) m.cells = new Array<number>(n).fill(0);
      expandRuns(mm.runs, m.cells);
      m.heads = mm.heads.map((hd) => ({ ...hd }));
      m.version++;
    }
  }

  /**
   * Once per drawn frame, before draining events: applies the remote frames' world changes whose
   * tick the render tick `rt` has reached, and drops the heads that left by then.
   */
  advance(rt: number): void {
    this.flushUpTo(rt);
  }

  /** Applies every deferred remote change now (warm-ups, tests, before an own change). */
  flushDeferred(): void {
    this.flushUpTo(Number.POSITIVE_INFINITY);
  }

  /** Remote frames still waiting for the render tick (tests, debug). */
  get deferredFrames(): number {
    return this.deferred.length;
  }

  private flushUpTo(rt: number): void {
    const d = this.deferred;
    let n = 0;
    while (n < d.length && (d[n] as DeferredWorld).tick <= rt) n++;
    if (n > 0) for (const w of d.splice(0, n)) this.applyWorld(w);
    for (const [id, hd] of this.heads) {
      if (hd.goneTick >= 0 && hd.goneTick <= rt) this.heads.delete(id);
    }
  }

  /** True for an event of the own life (it applies on arrival, with its whole frame). */
  private ownsEvent(e: ArenaEventRecord): boolean {
    const own = this.own.playerId;
    if (own === 0) return false;
    switch (e.type) {
      case ARENA_EVENT.DEATH:
        return e.victimId === own || e.killerId === own;
      case ARENA_EVENT.SPILL:
        return e.victimId === own;
      default:
        return e.playerId === own;
    }
  }

  /** One frame's land, trails, drops and events, in the order the frame carries them. */
  private applyWorld(w: DeferredWorld): void {
    if (w.events.length > 0) {
      this.snapshotDeaths(w.events);
      this.snapshotExits(w.events);
    }
    const own = this.own.playerId;
    const ownCapture =
      own !== 0 && w.events.some((e) => e.type === ARENA_EVENT.CAPTURE && e.playerId === own);
    if (w.land) {
      const before = ownCapture ? this.notOwnedIn(w.land.chunks) : null;
      this.applyLand(w.land.snapshot, w.land.chunks);
      if (before) {
        const cells = before.filter((c) => this.owner[c] === own);
        this.ownGain = { cells, atMs: w.atMs };
      }
    }
    if (w.trails.length > 0) this.applyTrails(w.trails);
    if (w.drops) this.applyDrops(w.drops.added, w.drops.removed, w.atMs);
    for (const event of w.events) this.events.push({ event, tick: w.tick, atMs: w.atMs });
  }

  /** Events since the last drain, oldest first (the queue is emptied). */
  takeEvents(): QueuedArenaEvent[] {
    const out = this.events;
    this.events = [];
    return out;
  }

  /** The snapshot of a player who died in a frame since the last drain (and forgets it). */
  takeDeathSnapshot(victimId: number): DeathSnapshot | null {
    const d = this.deaths.get(victimId) ?? null;
    this.deaths.delete(victimId);
    return d;
  }

  /** The land of a player who exited in a frame since the last drain (the exit fade). */
  takeExitSnapshot(playerId: number): { readonly color: Rgb; readonly cells: number[] } | null {
    const x = this.exits.get(playerId) ?? null;
    this.exits.delete(playerId);
    return x;
  }

  /** Drops removed since the last drain (collect effects). */
  takeRemovedDrops(): RemovedDrop[] {
    const out = this.removedDrops;
    this.removedDrops = [];
    return out;
  }

  /** Forgets everything but the WELCOME info (a new socket, a resync). */
  reset(): void {
    this.owner.fill(0);
    this.landEpoch++;
    this.landVersion++;
    for (let i = 0; i < this.chunkVersion.length; i++) {
      this.chunkVersion[i] = (this.chunkVersion[i] as number) + 1;
    }
    this.players.clear();
    this.values.clear();
    this.leaderboard = null;
    this.leaderboardVersion++;
    this.minimap.size = 0;
    this.minimap.cells = [];
    this.minimap.heads = [];
    this.minimap.version++;
    this.resetDynamic();
    this.setOwn(0);
    this.lastTick = -1;
  }

  private resetDynamic(): void {
    this.heads.clear();
    this.trails.clear();
    this.drops.clear();
    this.events = [];
    this.deferred = [];
    this.exits.clear();
    this.removedDrops = [];
    this.deaths.clear();
    this.ownGain = null;
  }

  /** Records the land boundary and trail of every DEATH victim before the frame clears them. */
  private snapshotDeaths(events: readonly ArenaEventRecord[]): void {
    const victims = new Map<number, number[]>();
    for (const e of events) {
      if (e.type === ARENA_EVENT.DEATH && e.victimId !== 0) victims.set(e.victimId, []);
    }
    if (victims.size === 0) return;
    const { cols, rows, owner } = this;
    for (let cy = 0; cy < rows; cy++) {
      const row = cy * cols;
      for (let cx = 0; cx < cols; cx++) {
        const id = owner[row + cx] as number;
        if (id === 0) continue;
        const edges = victims.get(id);
        if (!edges || edges.length >= DEATH_EDGES_MAX * 4) continue;
        if (cy === 0 || owner[row - cols + cx] !== id) edges.push(cx + 0.5, cy, 0, -1);
        if (cy === rows - 1 || owner[row + cols + cx] !== id) edges.push(cx + 0.5, cy + 1, 0, 1);
        if (cx === 0 || owner[row + cx - 1] !== id) edges.push(cx, cy + 0.5, -1, 0);
        if (cx === cols - 1 || owner[row + cx + 1] !== id) edges.push(cx + 1, cy + 0.5, 1, 0);
      }
    }
    for (const [victimId, edges] of victims) {
      this.deaths.set(victimId, {
        victimId,
        color: this.colorOf(victimId),
        edges,
        trail: [...this.trailOf(victimId)],
      });
    }
  }

  /** Records the land cells of every EXIT player before the frame clears them (the exit fade). */
  private snapshotExits(events: readonly ArenaEventRecord[]): void {
    const ids = new Set<number>();
    for (const e of events)
      if (e.type === ARENA_EVENT.EXIT && e.playerId !== 0) ids.add(e.playerId);
    if (ids.size === 0) return;
    const cells = new Map<number, number[]>();
    for (const id of ids) cells.set(id, []);
    const owner = this.owner;
    for (let i = 0; i < owner.length; i++) {
      const id = owner[i] as number;
      if (id !== 0) cells.get(id)?.push(i);
    }
    for (const [id, list] of cells) this.exits.set(id, { color: this.colorOf(id), cells: list });
  }

  /** Cells in `chunks` not owned by the own player (before a frame applies them). */
  private notOwnedIn(chunks: readonly { chunk: number }[]): number[] {
    const out: number[] = [];
    const own = this.own.playerId;
    const s = ARENA_NET.chunkSize;
    for (const c of chunks) {
      const kx = c.chunk % this.chunksX;
      const ky = Math.floor(c.chunk / this.chunksX);
      for (let dy = 0; dy < s; dy++) {
        const cy = ky * s + dy;
        if (cy >= this.rows) break;
        for (let dx = 0; dx < s; dx++) {
          const cx = kx * s + dx;
          if (cx >= this.cols) break;
          const i = cy * this.cols + cx;
          if (this.owner[i] !== own) out.push(i);
        }
      }
    }
    return out;
  }

  // --- land ----------------------------------------------------------------------------------

  /** Owner of cell (cx, cy); 0 outside the grid. */
  ownerAt(cx: number, cy: number): number {
    if (cx < 0 || cy < 0 || cx >= this.cols || cy >= this.rows) return 0;
    return this.owner[cy * this.cols + cx] as number;
  }

  /** Owner of the cell holding world point (x, y). */
  ownerAtPoint(x: number, y: number): number {
    return this.ownerAt(Math.floor(x), Math.floor(y));
  }

  /** Sets one cell (the demo arena and tests write land this way). */
  setCell(cx: number, cy: number, id: number): void {
    if (cx < 0 || cy < 0 || cx >= this.cols || cy >= this.rows) return;
    const i = cy * this.cols + cx;
    if (this.owner[i] === id) return;
    this.owner[i] = id;
    this.touchChunkOfCell(cx, cy);
  }

  /** Marks the chunk holding (cx, cy) and its neighbours changed. */
  touchChunkOfCell(cx: number, cy: number): void {
    const s = ARENA_NET.chunkSize;
    this.touchChunk(Math.floor(cx / s), Math.floor(cy / s));
  }

  private touchChunk(kx: number, ky: number): void {
    for (let dy = -1; dy <= 1; dy++) {
      const y = ky + dy;
      if (y < 0 || y >= this.chunksY) continue;
      for (let dx = -1; dx <= 1; dx++) {
        const x = kx + dx;
        if (x < 0 || x >= this.chunksX) continue;
        const i = y * this.chunksX + x;
        this.chunkVersion[i] = (this.chunkVersion[i] as number) + 1;
      }
    }
    this.landVersion++;
  }

  private applyLand(snapshot: boolean, chunks: readonly { chunk: number; runs: number[] }[]): void {
    if (snapshot) {
      this.owner.fill(0);
      this.landEpoch++;
      this.landVersion++;
      for (let i = 0; i < this.chunkVersion.length; i++) {
        this.chunkVersion[i] = (this.chunkVersion[i] as number) + 1;
      }
    }
    for (const c of chunks) {
      if (c.chunk < 0 || c.chunk >= this.chunkVersion.length) continue;
      try {
        applyChunkRuns(c.runs, c.chunk, this.cols, this.rows, this.owner);
      } catch {
        continue; // the reader already rejects bad runs; never let one chunk stop the frame
      }
      this.touchChunk(c.chunk % this.chunksX, Math.floor(c.chunk / this.chunksX));
    }
  }

  // --- heads ---------------------------------------------------------------------------------

  private applyHeads(records: readonly ArenaHeadRecord[] | undefined, tick: number): void {
    if (records) {
      const seen = new Set<number>();
      for (const r of records) {
        seen.add(r.playerId);
        let hd = this.heads.get(r.playerId);
        if (!hd) {
          hd = {
            playerId: r.playerId,
            samples: [],
            shielded: false,
            cashingOut: false,
            frozen: false,
            autopilot: false,
            alarm: false,
            progress: 0,
            lastTick: tick,
            firstTick: tick,
            goneTick: -1,
          };
          this.heads.set(r.playerId, hd);
        }
        const s = hd.samples;
        const last = s[s.length - 1];
        const sample = { tick, x: r.x, y: r.y, heading: headingFromWire(r.heading) };
        if (last && last.tick === tick) s[s.length - 1] = sample;
        else {
          s.push(sample);
          if (s.length > HEAD_SAMPLES) s.shift();
        }
        hd.shielded = r.shielded;
        hd.cashingOut = r.cashingOut;
        hd.frozen = r.frozen;
        hd.autopilot = r.autopilot;
        hd.alarm = r.alarm;
        hd.progress = r.cashingOut ? r.progress : 0;
        hd.lastTick = tick;
        hd.goneTick = -1;
        if (r.playerId === this.own.playerId && this.own.playerId !== 0) {
          this.own.head = r;
          this.own.headTick = tick;
          this.own.headVersion++;
          this.own.alarm = r.alarm;
        }
      }
      // A head no longer listed is drawn until the render tick reaches this frame (the own head
      // is drawn by the predictor and goes at once).
      const own = this.own.playerId;
      for (const [id, hd] of this.heads) {
        if (seen.has(id)) continue;
        if (id === own) this.heads.delete(id);
        else if (hd.goneTick < 0) hd.goneTick = tick;
      }
    } else {
      for (const [id, hd] of this.heads) {
        if (tick - hd.lastTick > HEAD_STALE_TICKS) this.heads.delete(id);
      }
    }
  }

  /** A head's interpolated position at fractional render tick `rt`, or null if unknown. */
  sampleHead(playerId: number, rt: number): HeadSample | null {
    const hd = this.heads.get(playerId);
    if (!hd || hd.samples.length === 0) return null;
    return interpolateSamples(hd.samples, rt);
  }

  // --- trails --------------------------------------------------------------------------------

  private applyTrails(
    records: readonly { playerId: number; op: number; startCell: number; steps: number[] }[],
  ): void {
    for (const r of records) {
      let tr = this.trails.get(r.playerId);
      if (r.op === ARENA_TRAIL_OP.CLEAR) {
        if (tr) {
          tr.cells.length = 0;
          tr.epoch++;
        }
        continue;
      }
      if (r.op === ARENA_TRAIL_OP.RESET) {
        if (!tr) {
          tr = { cells: [], epoch: 0 };
          this.trails.set(r.playerId, tr);
        } else {
          tr.epoch++;
        }
        tr.cells.length = 0;
        tr.cells.push(r.startCell);
        applyTrailSteps(r.startCell, r.steps, this.cols, tr.cells);
        continue;
      }
      // APPEND: continue from the last cell; an append to a trail this client never saw waits
      // for the server's RESET.
      if (!tr || tr.cells.length === 0) continue;
      applyTrailSteps(tr.cells[tr.cells.length - 1] as number, r.steps, this.cols, tr.cells);
    }
  }

  trailOf(playerId: number): readonly number[] {
    return this.trails.get(playerId)?.cells ?? EMPTY;
  }

  // --- drops ---------------------------------------------------------------------------------

  private applyDrops(
    added: readonly { dropId: number; cell: number; value: number }[],
    removed: readonly ArenaDropRemove[],
    nowMs: number,
  ): void {
    for (const a of added) {
      this.drops.set(a.dropId, { dropId: a.dropId, cell: a.cell, value: a.value, atMs: nowMs });
    }
    for (const r of removed) {
      const d = this.drops.get(r.dropId);
      if (!d) continue;
      this.drops.delete(r.dropId);
      this.removedDrops.push({ ...r, cell: d.cell, value: d.value, atMs: nowMs });
    }
  }

  // --- people --------------------------------------------------------------------------------

  nameOf(playerId: number): string {
    return this.players.get(playerId)?.name ?? '';
  }

  /** The player's arena skin id (`""` = none or unknown player). */
  skinOf(playerId: number): string {
    return this.players.get(playerId)?.skinId ?? '';
  }

  colorOf(playerId: number): Rgb {
    return this.players.get(playerId)?.color ?? FALLBACK_COLOR;
  }

  valueOf(playerId: number): number | null {
    return this.values.get(playerId) ?? null;
  }
}

const EMPTY: readonly number[] = Object.freeze([]);
const FALLBACK_COLOR: Rgb = 0x8c8c8c;

/** Interpolates `samples` (oldest first) at fractional tick `rt`; extrapolates ≤ 1 tick. */
export function interpolateSamples(samples: readonly HeadSample[], rt: number): HeadSample {
  const first = samples[0] as HeadSample;
  const last = samples[samples.length - 1] as HeadSample;
  if (samples.length === 1 || rt <= first.tick) return { ...first, tick: rt };
  if (rt >= last.tick) {
    const prev = samples[samples.length - 2] as HeadSample;
    const span = last.tick - prev.tick;
    if (!(span > 0)) return { ...last, tick: rt };
    const ahead = Math.min(rt - last.tick, HEAD_EXTRAPOLATE_TICKS) / span;
    return {
      tick: rt,
      x: last.x + (last.x - prev.x) * ahead,
      y: last.y + (last.y - prev.y) * ahead,
      heading: last.heading,
    };
  }
  for (let i = 1; i < samples.length; i++) {
    const b = samples[i] as HeadSample;
    if (rt > b.tick) continue;
    const a = samples[i - 1] as HeadSample;
    const t = b.tick > a.tick ? (rt - a.tick) / (b.tick - a.tick) : 1;
    return {
      tick: rt,
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t,
      heading: lerpAngle(a.heading, b.heading, t),
    };
  }
  return { ...last, tick: rt };
}

/** Whether a queued event is too old to show or play when drained at `nowMs` (C-15, C-23). */
export function isStaleEvent(atMs: number, nowMs: number): boolean {
  return nowMs - atMs > ARENA_CLIENT.staleEventMs;
}
